/**
 * RedstoneSystem -- Propagacion basica de energia redstone.
 *
 * Fuentes de energia: palanca (toggle), boton (pulso 1s), placa de presion (mientras se pisa), antorcha de redstone (siempre encendida)
 * Transmision: El cable de redstone lleva senal hasta 15 bloques, debilitandose 1 por bloque
 * Receptores: Las puertas se abren/cierran, el TNT se enciende
 *
 * Diseno: Al cambiar cualquier bloque relacionado con redstone, recalcular niveles de energia
 * en cables cercanos. Propagacion BFS simple.
 */

import { BlockId, isDoor, isRedstoneComponent } from './BlockType.js';

export class RedstoneSystem {
  constructor(world) {
    this.world = world;
    this._powerMap = new Map(); // "x,y,z" -> powerLevel (0-15)
    this._buttonTimers = new Map(); // "x,y,z" -> timeLeft (seconds)
    this._pressurePlates = new Map(); // "x,y,z" -> active boolean
    this._leverStates = new Map(); // "x,y,z" -> on boolean
    this._activeReceivers = new Map(); // "x,y,z" -> previous power state (for toggle detection)
  }

  /**
   * Clave de posicion para mapas internos
   */
  _key(x, y, z) {
    return `${x},${y},${z}`;
  }

  /**
   * Llamado cuando un componente de redstone se coloca o cambia
   */
  onBlockChanged(x, y, z, blockId) {
    if (blockId === BlockId.AIR) {
      // Bloque removido - limpiar estados
      const key = this._key(x, y, z);
      this._leverStates.delete(key);
      this._buttonTimers.delete(key);
      this._pressurePlates.delete(key);
      this._powerMap.delete(key);
    }
    // Recalcular propagacion en la zona
    this._recalculateArea(x, y, z);
  }

  /**
   * Alternar una palanca en la posicion dada
   * @returns {boolean} nuevo estado (true = encendida)
   */
  toggleLever(x, y, z) {
    const blockId = this.world.getBlock(x, y, z);
    if (blockId !== BlockId.LEVER) return false;

    const key = this._key(x, y, z);
    const currentState = this._leverStates.get(key) || false;
    const newState = !currentState;
    this._leverStates.set(key, newState);

    // Recalcular propagacion
    this._recalculateArea(x, y, z);
    return newState;
  }

  /**
   * Presionar un boton (pulso de 1 segundo)
   */
  pressButton(x, y, z) {
    const blockId = this.world.getBlock(x, y, z);
    if (blockId !== BlockId.BUTTON) return;

    const key = this._key(x, y, z);
    // Activar el boton por 1 segundo
    this._buttonTimers.set(key, 1.0);

    // Recalcular propagacion
    this._recalculateArea(x, y, z);
  }

  /**
   * Verificar placas de presion cerca del jugador
   */
  updatePressurePlates(playerPos) {
    const px = Math.floor(playerPos.x);
    const py = Math.floor(playerPos.y);
    const pz = Math.floor(playerPos.z);

    // Verificar un area de 3x1x3 alrededor de los pies del jugador
    const checkRadius = 1;
    for (let dx = -checkRadius; dx <= checkRadius; dx++) {
      for (let dz = -checkRadius; dz <= checkRadius; dz++) {
        // La placa de presion esta en el bloque debajo de los pies o al nivel de los pies
        for (let dy = -1; dy <= 0; dy++) {
          const bx = px + dx;
          const by = py + dy;
          const bz = pz + dz;
          const blockId = this.world.getBlock(bx, by, bz);

          if (blockId === BlockId.PRESSURE_PLATE) {
            const key = this._key(bx, by, bz);
            // Verificar si el jugador esta realmente sobre la placa
            const onPlate = (Math.abs(playerPos.x - (bx + 0.5)) < 0.8) &&
                            (Math.abs(playerPos.z - (bz + 0.5)) < 0.8) &&
                            (playerPos.y >= by && playerPos.y <= by + 1.5);

            const wasActive = this._pressurePlates.get(key) || false;

            if (onPlate && !wasActive) {
              this._pressurePlates.set(key, true);
              this._recalculateArea(bx, by, bz);
            } else if (!onPlate && wasActive) {
              this._pressurePlates.set(key, false);
              this._recalculateArea(bx, by, bz);
            }
          }
        }
      }
    }

    // Desactivar placas lejanas que ya no tienen al jugador encima
    for (const [key, active] of this._pressurePlates.entries()) {
      if (!active) continue;
      const [bx, by, bz] = key.split(',').map(Number);
      const dist = Math.abs(playerPos.x - (bx + 0.5)) + Math.abs(playerPos.z - (bz + 0.5));
      if (dist > 2) {
        this._pressurePlates.set(key, false);
        this._recalculateArea(bx, by, bz);
      }
    }
  }

  /**
   * Actualizar temporizadores de botones
   */
  update(dt) {
    const expired = [];

    for (const [key, timeLeft] of this._buttonTimers.entries()) {
      const newTime = timeLeft - dt;
      if (newTime <= 0) {
        expired.push(key);
      } else {
        this._buttonTimers.set(key, newTime);
      }
    }

    // Desactivar botones expirados y recalcular
    for (const key of expired) {
      this._buttonTimers.delete(key);
      const [x, y, z] = key.split(',').map(Number);
      this._recalculateArea(x, y, z);
    }
  }

  /**
   * Obtener nivel de energia en una posicion
   * @returns {number} 0-15
   */
  getPower(x, y, z) {
    return this._powerMap.get(this._key(x, y, z)) || 0;
  }

  /**
   * Verificar si una posicion es fuente de energia
   * @returns {number} nivel de energia (0 o 15)
   */
  _getSourcePower(x, y, z) {
    const blockId = this.world.getBlock(x, y, z);

    switch (blockId) {
      case BlockId.LEVER: {
        const key = this._key(x, y, z);
        return (this._leverStates.get(key) || false) ? 15 : 0;
      }
      case BlockId.BUTTON: {
        const key = this._key(x, y, z);
        return this._buttonTimers.has(key) ? 15 : 0;
      }
      case BlockId.PRESSURE_PLATE: {
        const key = this._key(x, y, z);
        return (this._pressurePlates.get(key) || false) ? 15 : 0;
      }
      case BlockId.REDSTONE_TORCH:
        return 15; // Siempre encendida
      default:
        return 0;
    }
  }

  /**
   * Recalcular energia en un area alrededor de un punto.
   * Primero limpia los niveles de energia, luego propaga desde fuentes.
   */
  _recalculateArea(cx, cy, cz) {
    const RANGE = 16; // Alcance maximo de recalculacion

    // Paso 1: Limpiar niveles de energia en cables del area
    for (let dx = -RANGE; dx <= RANGE; dx++) {
      for (let dy = -RANGE; dy <= RANGE; dy++) {
        for (let dz = -RANGE; dz <= RANGE; dz++) {
          const x = cx + dx;
          const y = cy + dy;
          const z = cz + dz;
          const blockId = this.world.getBlock(x, y, z);
          if (blockId === BlockId.REDSTONE_WIRE) {
            this._powerMap.delete(this._key(x, y, z));
          }
        }
      }
    }

    // Paso 2: Encontrar todas las fuentes de energia en el area
    const sources = [];
    for (let dx = -RANGE; dx <= RANGE; dx++) {
      for (let dy = -RANGE; dy <= RANGE; dy++) {
        for (let dz = -RANGE; dz <= RANGE; dz++) {
          const x = cx + dx;
          const y = cy + dy;
          const z = cz + dz;
          const power = this._getSourcePower(x, y, z);
          if (power > 0) {
            sources.push({ x, y, z, power });
          }
        }
      }
    }

    // Paso 3: Propagar desde cada fuente usando BFS
    for (const source of sources) {
      this._propagate(source.x, source.y, source.z, source.power);
    }

    // Paso 4: Aplicar energia a receptores (puertas, TNT, etc.)
    this._applyPowerToReceivers(cx, cy, cz, RANGE);
  }

  /**
   * Propagar energia desde una fuente usando BFS
   * Solo se propaga a traves de bloques REDSTONE_WIRE
   */
  _propagate(startX, startY, startZ, initialPower) {
    // Direcciones adyacentes (6 direcciones cardinales)
    const dirs = [
      [1, 0, 0], [-1, 0, 0],
      [0, 1, 0], [0, -1, 0],
      [0, 0, 1], [0, 0, -1],
    ];

    // BFS queue: { x, y, z, power }
    const queue = [];

    // Primero, energizar los cables adyacentes a la fuente
    for (const [dx, dy, dz] of dirs) {
      const nx = startX + dx;
      const ny = startY + dy;
      const nz = startZ + dz;
      const blockId = this.world.getBlock(nx, ny, nz);

      if (blockId === BlockId.REDSTONE_WIRE) {
        const key = this._key(nx, ny, nz);
        const currentPower = this._powerMap.get(key) || 0;
        const newPower = initialPower - 1;
        if (newPower > currentPower) {
          this._powerMap.set(key, newPower);
          queue.push({ x: nx, y: ny, z: nz, power: newPower });
        }
      }
    }

    // BFS a traves de cables de redstone
    while (queue.length > 0) {
      const current = queue.shift();
      if (current.power <= 0) continue;

      for (const [dx, dy, dz] of dirs) {
        const nx = current.x + dx;
        const ny = current.y + dy;
        const nz = current.z + dz;
        const blockId = this.world.getBlock(nx, ny, nz);

        if (blockId === BlockId.REDSTONE_WIRE) {
          const key = this._key(nx, ny, nz);
          const currentPower = this._powerMap.get(key) || 0;
          const newPower = current.power - 1;
          if (newPower > currentPower) {
            this._powerMap.set(key, newPower);
            queue.push({ x: nx, y: ny, z: nz, power: newPower });
          }
        }
      }
    }
  }

  /**
   * Aplicar energia a receptores en el area (puertas, TNT, etc.)
   */
  _applyPowerToReceivers(cx, cy, cz, range) {
    const dirs = [
      [1, 0, 0], [-1, 0, 0],
      [0, 1, 0], [0, -1, 0],
      [0, 0, 1], [0, 0, -1],
    ];

    // Buscar todos los bloques con energia y ver si tienen receptores adyacentes
    for (let dx = -range; dx <= range; dx++) {
      for (let dy = -range; dy <= range; dy++) {
        for (let dz = -range; dz <= range; dz++) {
          const x = cx + dx;
          const y = cy + dy;
          const z = cz + dz;

          // Verificar si este bloque tiene energia (fuente o cable)
          const sourcePower = this._getSourcePower(x, y, z);
          const wirePower = this.getPower(x, y, z);
          const hasPower = sourcePower > 0 || wirePower > 0;

          if (!hasPower) continue;

          // Verificar receptores adyacentes
          for (const [ddx, ddy, ddz] of dirs) {
            const rx = x + ddx;
            const ry = y + ddy;
            const rz = z + ddz;
            this._applyPower(rx, ry, rz, true);
          }
        }
      }
    }

    // Tambien verificar receptores que PERDIERON energia
    for (const [key, wasPowered] of this._activeReceivers.entries()) {
      if (!wasPowered) continue;
      const [rx, ry, rz] = key.split(',').map(Number);

      // Verificar si todavia tiene energia adyacente
      let stillPowered = false;
      for (const [ddx, ddy, ddz] of dirs) {
        const ax = rx + ddx;
        const ay = ry + ddy;
        const az = rz + ddz;
        const sp = this._getSourcePower(ax, ay, az);
        const wp = this.getPower(ax, ay, az);
        if (sp > 0 || wp > 0) {
          stillPowered = true;
          break;
        }
      }

      if (!stillPowered) {
        this._applyPower(rx, ry, rz, false);
      }
    }
  }

  /**
   * Aplicar estado de energia a un receptor individual
   */
  _applyPower(x, y, z, powered) {
    const blockId = this.world.getBlock(x, y, z);
    const key = this._key(x, y, z);
    const wasPowered = this._activeReceivers.get(key) || false;

    // Puerta: abrir cuando recibe energia, cerrar cuando la pierde
    if (isDoor(blockId)) {
      if (powered && !wasPowered) {
        // Energizar -> abrir puerta
        this._activeReceivers.set(key, true);
        if (blockId === BlockId.DOOR_CLOSED) {
          this.world.setBlock(x, y, z, BlockId.DOOR_OPEN);
          // Tambien abrir la otra mitad de la puerta
          const above = this.world.getBlock(x, y + 1, z);
          const below = this.world.getBlock(x, y - 1, z);
          if (isDoor(above)) this.world.setBlock(x, y + 1, z, BlockId.DOOR_OPEN);
          if (isDoor(below)) this.world.setBlock(x, y - 1, z, BlockId.DOOR_OPEN);
        }
      } else if (!powered && wasPowered) {
        // Sin energia -> cerrar puerta
        this._activeReceivers.set(key, false);
        if (blockId === BlockId.DOOR_OPEN) {
          this.world.setBlock(x, y, z, BlockId.DOOR_CLOSED);
          const above = this.world.getBlock(x, y + 1, z);
          const below = this.world.getBlock(x, y - 1, z);
          if (isDoor(above)) this.world.setBlock(x, y + 1, z, BlockId.DOOR_CLOSED);
          if (isDoor(below)) this.world.setBlock(x, y - 1, z, BlockId.DOOR_CLOSED);
        }
      }
      return;
    }

    // TNT: al recibir energia, se enciende (se elimina el bloque, dejando que main.js maneje la explosion)
    if (blockId === BlockId.TNT && powered && !wasPowered) {
      this._activeReceivers.set(key, true);
      this.world.setBlock(x, y, z, BlockId.AIR);
      // La explosion sera manejada por el callback onTNTIgnite si esta definido
      if (this.onTNTIgnite) {
        this.onTNTIgnite(x, y, z);
      }
      return;
    }

    // Piston: extend/retract when powered/unpowered
    if (blockId === BlockId.PISTON || blockId === BlockId.STICKY_PISTON) {
      if (powered !== wasPowered) {
        this._activeReceivers.set(key, powered);
        if (this.onPistonPower) {
          this.onPistonPower(x, y, z, powered);
        }
      }
      return;
    }

    // Redstone Lamp: toggle lit/unlit
    if (blockId === BlockId.REDSTONE_LAMP || blockId === BlockId.REDSTONE_LAMP_LIT) {
      if (powered && !wasPowered) {
        this._activeReceivers.set(key, true);
        this.world.setBlock(x, y, z, BlockId.REDSTONE_LAMP_LIT);
      } else if (!powered && wasPowered) {
        this._activeReceivers.set(key, false);
        this.world.setBlock(x, y, z, BlockId.REDSTONE_LAMP);
      }
      return;
    }

    // Dispenser/Dropper: fire on rising edge
    if ((blockId === BlockId.DISPENSER || blockId === BlockId.DROPPER) && powered && !wasPowered) {
      this._activeReceivers.set(key, true);
      if (this.onDispenserFire) {
        this.onDispenserFire(x, y, z, blockId);
      }
      return;
    }

    // Actualizar estado del receptor
    if (powered) {
      this._activeReceivers.set(key, true);
    } else {
      this._activeReceivers.delete(key);
    }
  }

  /**
   * Callback opcional para cuando se enciende TNT via redstone
   * Se configura desde main.js
   * @type {Function|null}
   */
  onTNTIgnite = null;

  /**
   * Limpiar todo el estado del sistema
   */
  clear() {
    this._powerMap.clear();
    this._buttonTimers.clear();
    this._pressurePlates.clear();
    this._leverStates.clear();
    this._activeReceivers.clear();
  }

  /**
   * Serializar estado para guardado
   */
  serialize() {
    return {
      leverStates: Array.from(this._leverStates.entries()),
    };
  }

  /**
   * Deserializar estado desde guardado
   */
  deserialize(data) {
    if (!data) return;
    this.clear();
    if (data.leverStates) {
      for (const [key, value] of data.leverStates) {
        this._leverStates.set(key, value);
      }
      // Recalcular energia para todas las palancas guardadas
      for (const [key] of this._leverStates) {
        const [x, y, z] = key.split(',').map(Number);
        this._recalculateArea(x, y, z);
      }
    }
  }
}
